import type { S2PElevateShowcase, S2PElevateShowcaseExample } from "@/data/s2pElevateAI";
import { CORE_SHOWCASE_PRACTICE, ROCKLINE_SHOWCASE_PRACTICE } from "@/data/elevateShowcasePractice";

// Teaching examples, not measured results or evidence of customer deployment.
// Original source media and full departmental applications stay in the showcase data.
export const CORE_SHOWCASE_EXAMPLES: Record<string, S2PElevateShowcaseExample> = {
  "inbox-agent": {
    practice: CORE_SHOWCASE_PRACTICE["inbox-agent"],
    title: "Turn email threads into a reply queue",
    inputs: "Selected email threads and the approved documents needed to answer them.",
    work: "Ask ChatGPT to summarize requests, flag commitments, and draft replies from the supplied threads.",
    review: "Check the facts and recipient. A person decides what to send.",
    done: "Each proposed reply points to its thread and leaves the send decision with its owner.",
    artifact: { title: "Inbox review queue", lines: ["DECIDE · A customer asks to change a date", "DRAFT · Reply cites the agreed timeline", "REVIEW · Confirm the commitment before sending"] },
    applicationFunctions: ["Sales", "Operations", "Finance"],
  },
  "source-studio": {
    practice: CORE_SHOWCASE_PRACTICE["source-studio"],
    title: "Turn one source collection into learning assets",
    inputs: "A selected source collection with document titles, revisions, sections, and approval status.",
    work: "Verify the controlling revision, then use Claude to draft a briefing or checklist with source sections.",
    review: "Check the revision and each claim. Resolve conflicting guidance with the document owner before teaching it.",
    done: "The chosen format answers the learner's question and retains a path back to the source.",
    artifact: { title: "One approved source collection", lines: ["BRIEFING · What the team needs to know", "CHECKLIST · What to do at the point of work", "OVERVIEW · A different format for the same material"] },
    applicationFunctions: ["HR", "Operations", "Product"],
  },
  "microsoft-researcher-analyst": {
    practice: CORE_SHOWCASE_PRACTICE["microsoft-researcher-analyst"],
    title: "Match research and analysis to the question",
    inputs: "Approved documents or spreadsheets, plus a specific decision question.",
    work: "Ask ChatGPT to compare the evidence, analyze the data, and show the calculations behind its brief.",
    review: "Check sources, formulas, and assumptions before accepting the recommendation.",
    done: "The reviewer can trace the conclusion to a source or calculation and identify open questions.",
    artifact: { title: "Decision brief", lines: ["QUESTION · Which costs changed this period?", "EVIDENCE · Source rows and calculation", "REVIEW · Assumptions that still need an owner"] },
    applicationFunctions: ["Finance", "Operations", "Sales"],
  },
  "logged-in-browser": {
    practice: CORE_SHOWCASE_PRACTICE["logged-in-browser"],
    title: "Let an agent work through an approved browser task",
    inputs: "One approved site, a bounded task, and a person watching the first run.",
    work: "Read the page, navigate to the right record, and prepare the requested result.",
    review: "Pause before changes. Check the record, destination, and proposed action.",
    done: "The draft matches the source and the agent has stopped at the agreed decision point.",
    artifact: { title: "Supervised browser run", lines: ["READ · Open the approved record", "PREPARE · Draft the update with source evidence", "PAUSE · Ask the owner before saving"] },
    applicationFunctions: ["Sales", "Operations", "Marketing"],
  },
  "linkedin-prospecting": {
    practice: CORE_SHOWCASE_PRACTICE["linkedin-prospecting"],
    title: "Research a prospect and prepare the next message",
    inputs: "An approved prospect list, selection criteria, and permitted profile information.",
    work: "Compare profiles with the criteria and prepare a sourced reason to connect.",
    review: "Confirm identity, relevance, and wording before any invitation or message.",
    done: "A person receives a reviewable shortlist and drafts; nothing is sent during the example run.",
    artifact: { title: "Prospect review packet", lines: ["FIT · Evidence against the selection criteria", "DRAFT · A relevant reason to connect", "DECISION · Approve, revise, or skip"] },
    applicationFunctions: ["Sales", "HR", "Marketing"],
  },
  "admin-data-entry": {
    practice: CORE_SHOWCASE_PRACTICE["admin-data-entry"],
    title: "Prepare repeated form entries from a spreadsheet",
    inputs: "An approved spreadsheet, a field map, and one authorized destination form.",
    work: "Read each row, fill the matching fields, and flag missing or unexpected values.",
    review: "Compare the prepared entry with its row. Stop before submission or on a mismatch.",
    done: "Every prepared entry traces to a source row; exceptions are separated for review.",
    artifact: { title: "Entry review queue", lines: ["MATCH · Source columns mapped to form fields", "EXCEPTION · A required field is missing", "PAUSE · Review before submitting the entry"] },
    applicationFunctions: ["Finance", "Sales", "Operations"],
  },
  "reusable-skill": {
    practice: CORE_SHOWCASE_PRACTICE["reusable-skill"],
    title: "Save the method and improve the next run",
    inputs: "A recurring job, approved references, and one accepted example of the output.",
    work: "Reuse the instructions and references to prepare the next deliverable.",
    review: "Record what needed correction. Update the Skill and compare the next result.",
    done: "The same review checks show whether the correction reduced rework on the next run.",
    artifact: { title: "A correction becomes a reusable rule", lines: ["BEFORE · Staffing caused more overdue replies", "CORRECTION · The count rose; the cause is unverified", "AFTER · Separate observation, hypothesis, and question"] },
    applicationFunctions: ["Finance", "Sales", "Marketing"],
  },
  "scheduled-report": {
    practice: CORE_SHOWCASE_PRACTICE["scheduled-report"],
    title: "Repeat the report and measure what improves",
    inputs: "An approved reporting source, metric definition, prior result, and review cadence.",
    work: "Prepare the current report, compare periods, and flag material changes.",
    review: "Check freshness and figures. Approve scheduling only after repeated reviewed runs.",
    done: "Each run records the result, review effort, decision, and one change to test next time.",
    artifact: { title: "Recurring report review", lines: ["COMPARE · Current value and prior period", "CHECK · Freshness, filters, and source links", "IMPROVE · Record one change for the next run"] },
    applicationFunctions: ["Finance", "Sales", "Operations"],
  },
};

// Adapt the guide's workflow patterns to the main deck's tools. Keep the original
// guide data and stable IDs intact; never relabel another vendor's screenshot.
const CORE_SHOWCASE_OVERRIDES: Record<string, Partial<S2PElevateShowcase>> = {
  "inbox-agent": {
    platform: "ChatGPT",
    title: "Prepare an inbox triage and reply queue in ChatGPT",
    lead: "Provide approved email threads and reference files, then review the proposed replies before sending.",
    bullets: ["Supply only the threads and files needed for the task.", "Ask for requests, commitments, and reply drafts with source references.", "Keep the recipient and send decision with a person."],
    agentic: "The workflow prepares a review queue from supplied evidence. Additional source access must be configured separately.",
    implication: "Start with approved thread exports; confirm any connected email access before expanding the workflow.",
    sourceUrl: "https://help.openai.com/en/articles/9260256-chatgpt-capabilities-overview",
  },
  "source-studio": {
    platform: "Claude",
    title: "Turn project sources into learning drafts in Claude",
    lead: "Use approved project files and instructions to draft a briefing, study guide, or checklist for expert review.",
    bullets: ["Add the approved source files to a Claude Project.", "Specify the audience, format, and required source references.", "Check each important requirement before using the draft."],
    agentic: "Reusable project context supports repeated drafting from the same approved materials.",
    implication: "Keep the sources and review standard together so the next draft starts with the same context.",
    sourceUrl: "https://support.claude.com/en/articles/9519177-how-can-i-create-and-manage-projects",
  },
  "microsoft-researcher-analyst": {
    platform: "ChatGPT",
    title: "Build a sourced research and analysis brief in ChatGPT",
    lead: "Use approved documents and spreadsheets to investigate a question, with source references and calculations for review.",
    bullets: ["Define the question and provide the permitted evidence.", "Ask for source references, calculations, and unresolved assumptions.", "Verify the numbers before accepting the conclusion."],
    agentic: "The workflow combines document review and data analysis, with a person checking the evidence and deciding the next action.",
    implication: "Evaluate the brief against the same question and review criteria used in the current process.",
    image: undefined,
    imageAlt: undefined,
    sourceUrl: "https://help.openai.com/en/articles/9260256-chatgpt-capabilities-overview",
  },
};

export const applyCoreShowcase = (item: S2PElevateShowcase): S2PElevateShowcase => ({
  ...item,
  ...CORE_SHOWCASE_OVERRIDES[item.id],
  support: item.support.filter((row) => row.provider === "ChatGPT" || row.provider === "Claude"),
  example: CORE_SHOWCASE_EXAMPLES[item.id],
});

export const ROCKLINE_SHOWCASE_EXAMPLES: Record<string, S2PElevateShowcaseExample> = {
  "forecast-inventory": {
    practice: ROCKLINE_SHOWCASE_PRACTICE["forecast-inventory"],
    title: "Turn inventory changes into a decision brief",
    inputs: "Approved forecast, inventory, and order exports for the same reporting period.",
    work: "Ask Copilot to compare changes, identify exceptions, and link each observation to its source row.",
    review: "The planner checks assumptions and chooses what to investigate or change.",
    done: "A sourced exception list separates observed changes from explanations still to confirm.",
    artifact: { title: "Inventory exception brief", lines: ["OBSERVATION · Demand and stock signals differ", "EVIDENCE · Link the matching source rows", "QUESTION · Which assumption needs the planner's review?"] },
    applicationFunctions: ["Forecasting", "Inventory", "Supply Chain"],
  },
  "safety-procedures": {
    practice: ROCKLINE_SHOWCASE_PRACTICE["safety-procedures"],
    title: "Turn approved procedures into training drafts",
    inputs: "Current approved procedures, the audience, and the required learning format.",
    work: "Ask Copilot to prepare a briefing or checklist and trace each requirement to its source.",
    review: "The safety or EHS owner resolves gaps and approves the technical content.",
    done: "A clearly marked draft preserves source requirements and lists questions for the expert.",
    artifact: { title: "Procedure learning packet", lines: ["BRIEFING · What this role needs to know", "CHECKLIST · Steps linked to the approved procedure", "EXPERT REVIEW · Resolve conflicts before use"] },
    applicationFunctions: ["EHS", "HR", "Operations"],
  },
  "governed-knowledge": {
    practice: ROCKLINE_SHOWCASE_PRACTICE["governed-knowledge"],
    title: "Find the controlling document and show the source",
    inputs: "A specific workplace question and documents the current user is allowed to access.",
    work: "Ask Copilot to locate the relevant section, summarize it, and return its title and date.",
    review: "The content owner resolves missing, conflicting, or out-of-date guidance.",
    done: "The person can open the controlling source and see any unanswered question.",
    artifact: { title: "Sourced answer", lines: ["ANSWER · The relevant instruction", "SOURCE · Document, section, and revision date", "ESCALATE · Conflicting guidance needs the content owner"] },
    applicationFunctions: ["HR", "Operations", "Quality"],
  },
  "line-scheduling": {
    practice: ROCKLINE_SHOWCASE_PRACTICE["line-scheduling"],
    title: "Compare schedules while the planner keeps control",
    inputs: "Approved orders, line capacity, material availability, and changeover rules.",
    work: "Ask Copilot to prepare schedule options and make the constraints and tradeoffs visible.",
    review: "The planner checks feasibility and quality holds before any production change.",
    done: "The options preserve documented constraints and stop at a planner-reviewed recommendation.",
    artifact: { title: "Schedule comparison", lines: ["OPTION A · Show capacity and changeover tradeoffs", "OPTION B · Show due-date and material tradeoffs", "PLANNER · Decide before the production plan changes"] },
    applicationFunctions: ["Planning", "Operations", "Quality"],
  },
  "sourcing-contracts": {
    practice: ROCKLINE_SHOWCASE_PRACTICE["sourcing-contracts"],
    title: "Prepare a contract comparison for the right reviewer",
    inputs: "An approved agreement, reference template, and sourcing review checklist.",
    work: "Ask Copilot to locate differences, quote the controlling clauses, and draft review questions.",
    review: "Sourcing, finance, or counsel makes the commercial or legal decision.",
    done: "Every material issue has a clause reference, open question, and named reviewer.",
    artifact: { title: "Negotiation review packet", lines: ["DIFFERENCE · A term departs from the template", "EVIDENCE · Quote and link the relevant clause", "REVIEWER · Route the question before agreeing"] },
    applicationFunctions: ["Sourcing", "Finance", "Legal"],
  },
  "logistics-transportation": {
    practice: ROCKLINE_SHOWCASE_PRACTICE["logistics-transportation"],
    title: "Surface transport exceptions for human review",
    inputs: "Approved shipment, carrier, service, and cost data with a shared date range.",
    work: "Ask Copilot to group repeated exceptions and distinguish observations from possible causes.",
    review: "The logistics owner verifies the pattern and approves any routing or service decision.",
    done: "A sourced queue identifies the exceptions and next questions without changing a route.",
    artifact: { title: "Transportation exception queue", lines: ["PATTERN · Repeated delays on a lane", "CHECK · Source records and reason codes", "OWNER · Investigate before changing the route"] },
    applicationFunctions: ["Logistics", "Customer Service", "Finance"],
  },
  "quality-supplier": {
    practice: ROCKLINE_SHOWCASE_PRACTICE["quality-supplier"],
    title: "Improve the weekly supplier-quality brief",
    inputs: "Approved supplier-quality records, the prior brief, and the review checklist.",
    work: "Ask Copilot to repeat the checks and draft changes, evidence, and unresolved questions.",
    review: "The quality owner verifies the brief and records one correction to the instructions.",
    done: "The next brief is checked against the same standard, with review effort recorded.",
    artifact: { title: "A correction improves the next brief", lines: ["BEFORE · Supplier process drift caused the increase", "CORRECTION · No root-cause finding was supplied", "AFTER · Separate observation, hypothesis, and question"] },
    applicationFunctions: ["Quality", "Supplier Management", "Operations"],
  },
  "analytics-reporting": {
    practice: ROCKLINE_SHOWCASE_PRACTICE["analytics-reporting"],
    title: "Repeat the report with evidence of improvement",
    inputs: "Approved Excel, Power BI, or Databricks data, metric definitions, and comparison period.",
    work: "Ask Copilot to prepare calculations, charts, and a narrative linked to the underlying evidence.",
    review: "The data owner verifies numbers and assumptions before the report is shared.",
    done: "Accepted quality and review time can be compared across runs before scheduling is approved.",
    artifact: { title: "Report improvement record", lines: ["BASELINE · Record current review and correction effort", "NEXT RUN · Use the same metric and reporting scope", "DECISION · Continue, revise, or stop based on the result"] },
    applicationFunctions: ["Analytics", "Power BI", "Finance"],
  },
};

// Match the same teaching moments while retaining all eight Rockline use cases.
export const ROCKLINE_SHOWCASE_BY_CORE_ID: Record<string, string> = {
  "inbox-agent": "forecast-inventory",
  "source-studio": "safety-procedures",
  "microsoft-researcher-analyst": "governed-knowledge",
  "logged-in-browser": "line-scheduling",
  "linkedin-prospecting": "sourcing-contracts",
  "admin-data-entry": "logistics-transportation",
  "reusable-skill": "quality-supplier",
  "scheduled-report": "analytics-reporting",
};
